<template>
  <div class="wizard-overlay">
    <div class="wizard-modal">
      <div class="wizard-header">
        <button class="wizard-close" @click="closeWizard">✕</button>
        <h2 class="title"><lang labelKey="WIZARD-GO-LIVE-TITLE" /></h2>
        <h3 class="pharmacy-name subtitle">{{ pageTitle }}</h3>
      </div>

      <div class="wizard-steps-container">
        <!-- INTERMEDIATE STATES -->
        <div v-if="validationError" class="intermediate-step error-state">
          <div class="status-icon icon-error">
            <span class="material-symbols-outlined" style="font-size: 90px"
              >error</span
            >
          </div>
          <h4 class="h4-title"><lang labelKey="FEHLER-BEI-GO-LIVE" /></h4>
          <p class="status-message">{{ validationError }}</p>
          <ul v-if="validationActions.length" class="validation-actions">
            <li
              v-for="(action, index) in validationActions"
              :key="index"
              :class="{ success: action.success, error: !action.success }"
            >
              {{ action.name }}
              <span class="action-status">
                <span
                  v-if="action.success"
                  class="material-symbols-outlined status-icon-success"
                >
                  check_circle
                </span>
                <span
                  v-else
                  class="material-symbols-outlined status-icon-error"
                  :title="action.error"
                >
                  cancel
                </span>
              </span>
            </li>
          </ul>
          <div class="wizard-actions">
            <button class="button btn-back" @click="resetValidation">
              <lang labelKey="WIZARD-GO-LIVE-BACK-BTN" />
            </button>
          </div>
        </div>

        <div
          v-else-if="validationSuccess"
          class="intermediate-step success-state"
        >
          <div class="status-icon icon-success">
            <span class="material-symbols-outlined" style="font-size: 90px"
              >check_circle</span
            >
          </div>
          <h4 class="h4-title"><lang labelKey="GO-LIVE-ERFOLGREICH" /></h4>
          <ul v-if="validationActions.length" class="validation-actions">
            <li
              v-for="(action, index) in validationActions"
              :key="index"
              :class="{ success: action.success, error: !action.success }"
            >
              {{ action.name }}
              <span class="action-status">
                <span
                  v-if="action.success"
                  class="material-symbols-outlined status-icon-success"
                >
                  check_circle
                </span>
                <span
                  v-else
                  class="material-symbols-outlined status-icon-error"
                  :title="action.error"
                >
                cancel
                </span>
              </span>
            </li>
          </ul>
          <div class="wizard-actions">
            <button class="button btn-next" @click="confirmSuccess">
              <lang labelKey="WIZARD-GO-LIVE-FINISH-BTN" />
            </button>
          </div>
        </div>

        <!-- FORM STEP -->
        <div v-else class="wizard-form-content">
          <h4 class="h4-title"><lang labelKey="WIZARD-GO-LIVE-ENTER-LIVE-DOMAIN" /></h4>
          <div class="control">
            <label><lang labelKey="WIZARD-GO-LIVE-DOMAIN-LABEL" /></label>
            <input
              class="input"
              v-model="liveDomain"
              placeholder="https://..."
            />
          </div>

          <div class="wizard-actions">
            <button
              class="button wizard-button btn-next"
              @click="submitGoLive"
              :disabled="!canSubmit || isValidating"
            >
              <span v-if="isValidating" class="loading-content">
                <span class="spinner"></span> Wird geprüft…
              </span>
              <span v-else><lang labelKey="WIZARD-GO-LIVE-GO-LIVE-BTN" /></span>
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions } from "vuex";

export default {
  name: "GoLiveWizard",
  props: {
    siteId: { type: [String, Number], required: true },
    pageTitle: { type: String, required: true },
  },
  data() {
    return {
      liveDomain: "",
      isValidating: false,
      validationError: null,
      validationSuccess: false,
      validationActions: [],
    };
  },
  mounted() {
    document.body.style.overflow = "hidden";
  },
  computed: {
    canSubmit() {
      return (
        this.liveDomain &&
        this.liveDomain.trim().length > 0 &&
        /^https?:\/\//.test(this.liveDomain.trim())
      );
    },
  },
  beforeUnmount() {
    document.body.style.overflow = "";
  },
  methods: {
    ...mapActions(["validateGoLive"]),

    async submitGoLive() {
      this.isValidating = true;
      this.validationError = null;
      this.validationActions = [];

      try {
        const payload = {
          siteId: this.siteId,
          url: this.liveDomain,
        };

        const res = await this.validateGoLive(payload);

        if (res && res.success) {
          this.validationSuccess = true;
          this.validationActions = res.actions || [];
        } else {
          this.validationError =
            (res && res.error) || "Ein Fehler ist aufgetreten.";
          this.validationActions = res.actions || [];
        }
      } catch (err) {
        this.validationError = "API Error: Connection failed.";
      } finally {
        this.isValidating = false;
      }
    },

    resetValidation() {
      this.validationError = null;
      this.validationActions = [];
    },

    closeWizard() {
      this.$emit("close");
      document.body.style.overflow = "";
    },

    confirmSuccess() {
      this.closeWizard();
    },
  },
};
</script>

<style scoped>
.wizard-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.6);
  z-index: 9999;
  display: flex;
  justify-content: center;
  align-items: center;
}
.wizard-modal {
  background: #dee4e9;
  width: 100%;
  height: 100%;
  overflow-y: auto;
  position: relative;
}
.wizard-header {
  background-color: #ffffff;
  padding: 20px 40px;
  margin-bottom: 20px;
}
.wizard-close {
  position: absolute;
  top: 37px;
  right: 20px;
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
}
.wizard-steps-container {
  margin: 50px auto;
  width: 600px;
  background: #fff;
  padding: 40px;
  border-radius: 3px;
  min-height: 300px;
  display: flex;
  flex-direction: column;
}
.wizard-form-content {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  flex-grow: 1;
}
.control {
  margin-bottom: 20px;
}
.input {
  width: 100%;
  padding: 10px;
  border: 1px solid #ccc;
  border-radius: 4px;
}
.h4-title {
  font-size: 1.5rem;
  margin-bottom: 12px;
}
.wizard-actions {
  margin-top: 30px;
  display: flex;
  justify-content: center;
}
.btn-next {
  background: #45a7d6;
  color: #fff;
  max-width: 350px;
}
.btn-next:disabled {
  background: #aab;
  cursor: not-allowed;
}
.intermediate-step {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  flex-grow: 1;
  text-align: center;
}
.status-icon {
  width: 80px;
  height: 80px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 40px;
  margin-bottom: 20px;
  color: white;
}
.icon-success {
  background-color: #27ae60;
}
.icon-error {
  background-color: #e74c3c;
}
.status-message {
  font-size: 18px;
  margin-bottom: 30px;
  color: #555;
  max-width: 400px;
}
.validation-actions {
  list-style: none;
  padding: 0;
  margin-top: 24px;
  text-align: left;
  max-width: 600px;
  width: 100%;
  border-top: 1px solid #ccc;
  padding-top: 16px;
}
.validation-actions li {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 12px;
  margin-bottom: 8px;
  border-radius: 6px;
  font-size: 15px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
}
.validation-actions li.success {
  background-color: #e6f9ed;
  color: #276749;
}
.validation-actions li.error {
  background-color: #fde2e1;
  color: #9e2a2b;
}
.status-icon-success {
  color: #27ae60;
  font-size: 20px;
  padding-top: 5px;
}
.status-icon-error {
  color: #e74c3c;
  font-size: 20px;
  padding-top: 5px;
}
.spinner {
  width: 16px;
  height: 16px;
  border: 2px solid rgba(255, 255, 255, 0.4);
  border-top-color: #fff;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}
@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}
</style>
